import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';

import { TranslateService } from '@ngx-translate/core';

import { FrodoService } from 'src/app/shared/services';
import { NavigationService } from 'src/app/shared/services/navigation/navigation.service';
import { SystemUpgrade, UmpService } from 'src/app/shared/services/ump';
import { KILL_SWITCH_MODE, VIEW_TYPES } from 'src/app/shared/utils/constants';

import { selectSettingKillSwitchMode } from '../settings';

import * as actions from './global.actions';
import { selectSessionActive } from './global.selector';
import { Health, HealthStatus } from './interface';
import { ERROR_MESSAGES } from './message';

@Injectable()
export class GlobalEffects {
  constructor(
    private actions$: Actions,
    private frodoApiService: FrodoService,
    private translateService: TranslateService,
    private router: Router,
    private store: Store,
    private navigationService: NavigationService,
    private umpService: UmpService
  ) {}

  loadHealth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadHealth),
      switchMap(() =>
        this.frodoApiService.getHealth().pipe(
          map((health: Health) => {
            if (health.status === HealthStatus.ERROR && health.error) {
              Object.keys(health.error).forEach((service) => {
                let errorMessage = this.translateService.instant(ERROR_MESSAGES.UNABLE_TO_CONNECT, {
                  service,
                });

                if (health.error[service].message) {
                  const message = health.error[service].message;
                  errorMessage = this.translateService.instant(ERROR_MESSAGES.ERROR_MESSAGE, {
                    service,
                    message,
                  });
                }

                console.warn(errorMessage);
              });
            }
            return actions.loadHealthSuccess({ health });
          }),
          catchError((error) => {
            return of(
              actions.loadHealthSuccess({
                health: { status: HealthStatus.ERROR, error },
              })
            );
          })
        )
      )
    )
  );

  handleKillSwitch$ = createEffect(
    () =>
      combineLatest([
        this.store.select(selectSettingKillSwitchMode),
        this.store.select(selectSessionActive),
      ]).pipe(
        distinctUntilChanged(
          ([prevKillSwitchMode, prevSessionActive], [currKillSwitchMode, currSessionActive]) =>
            prevKillSwitchMode === currKillSwitchMode && prevSessionActive === currSessionActive
        ),
        tap(([killSwitchMode, isSessionActive]) => {
          const currentUrl = this.router.url;
          const suspendedUrl = `/${VIEW_TYPES.DEVICE_SUSPENDED}`;
          const isSuspendedPage = currentUrl === suspendedUrl;
          const isKillSwitchOn = killSwitchMode === KILL_SWITCH_MODE.ON;

          if (isSuspendedPage && isKillSwitchOn) return;

          const isBlockedFlow =
            currentUrl.includes(VIEW_TYPES.REAR_LOADING) ||
            (isSessionActive &&
              (currentUrl.includes(VIEW_TYPES.FRONT_LOADING) ||
                currentUrl.includes(VIEW_TYPES.RETAIL)));

          if (isKillSwitchOn && isBlockedFlow) {
            this.router.navigate([suspendedUrl]);
          } else if (!isKillSwitchOn && isSuspendedPage) {
            this.navigationService.navigateToRootRoute();
          }
        })
      ),
    { dispatch: false }
  );

  loadSystemUpgrade$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadSystemUpgrade),
      filter(() => this.umpService.isConfigured()),
      switchMap(() =>
        this.umpService.getUpgradeStatus().pipe(
          map((systemUpgrade: SystemUpgrade) => {
            return actions.loadSystemUpgradeSuccess({ systemUpgrade });
          }),
          catchError((error) => {
            console.warn(
              this.translateService.instant(ERROR_MESSAGES.UNABLE_TO_CONNECT, { service: 'UMP' })
            );
            return of(actions.loadSystemUpgradeFailure({ error }));
          })
        )
      )
    )
  );
}
